import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { baseQuery } from "../api"
import bbApi from "../../api"
import { EnrollmentResponse } from "../../pages/enroll/types"
import { EnrollApiBody } from "../../api/enroll"

const reducerPath = "enrollment"

const isEnrollmentResponse = (
  body: EnrollApiBody | null
): body is EnrollmentResponse =>
  body !== null &&
  "enrollment_id" in body &&
  typeof (body as EnrollmentResponse).enrollment_id === "number"

export const enrollmentApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath],
  endpoints: builder => ({
    lookupEnrollment: builder.query<
      EnrollmentResponse,
      { key?: string; id?: number }
    >({
      queryFn: async params => {
        let result
        if (params.id) {
          result = await bbApi.enroll.get(params.id)
        } else if (params.key) {
          result = await bbApi.enroll.lookupByKey(params.key)
        } else {
          return {
            error: {
              status: 400,
              data: {
                status: "error",
                error: "invalid_key",
                message: "No key or ID provided"
              }
            }
          }
        }

        if (result.networkError) {
          return {
            error: { status: 0, data: result.networkError }
          }
        }

        // 200, 202, 207 all carry a valid EnrollmentResponse
        if (
          result.status >= 200 &&
          result.status < 300 &&
          isEnrollmentResponse(result.body)
        ) {
          return { data: result.body }
        }

        return {
          error: { status: result.status, data: result.body }
        }
      },
      providesTags: result =>
        result ? [{ type: reducerPath, id: result.enrollment_id }] : []
    }),

    retryEnrollment: builder.mutation<EnrollmentResponse, number>({
      queryFn: async id => {
        const result = await bbApi.enroll.retry(id)

        if (result.networkError) {
          return { error: { status: 0, data: result.networkError } }
        }

        // 200 (complete) and 207 (partial) both return valid enrollment data
        if (
          (result.status === 200 || result.status === 207) &&
          isEnrollmentResponse(result.body)
        ) {
          return { data: result.body }
        }

        return { error: { status: result.status, data: result.body } }
      },
      invalidatesTags: (result, _error, id) => [{ type: reducerPath, id }]
    }),

    deleteEnrollment: builder.mutation<
      { status: string; message: string },
      number
    >({
      queryFn: async id => {
        const result = await bbApi.enroll.delete(id)

        if (result.networkError) {
          return { error: { status: 0, data: result.networkError } }
        }

        if (result.status >= 200 && result.status < 300) {
          return { data: result.body as any }
        }

        return { error: { status: result.status, data: result.body } }
      },
      invalidatesTags: (_result, _error, id) => [{ type: reducerPath, id }]
    })
  })
})

export const {
  useLookupEnrollmentQuery,
  useRetryEnrollmentMutation,
  useDeleteEnrollmentMutation
} = enrollmentApi
